/**
 * Bonding Curve Calculations for Opinion Markets
 * 
 * Uses Constant Product Market Maker (CPMM) formula: x * y = k
 * where:
 * - x = virtual_liquidity (SOL reserves)
 * - y = total_shares (share reserves)
 * - k = constant product
 * 
 * When buying shares:
 * - SOL is added to reserves (x increases)
 * - Shares are removed from reserves (y decreases)
 * - Formula: shares_out = y - (k / (x + sol_in))
 * 
 * When selling shares:
 * - Shares are added to reserves (y increases)
 * - SOL is removed from reserves (x decreases)
 * - Formula: sol_out = x - (k / (y + shares_in))
 */

import { LAMPORTS_PER_SOL } from '@solana/web3.js';

export interface BondingCurveState {
  virtualLiquidity: number; // in lamports
  totalTokens: number;
  totalDeposits: number; // in lamports
}

export interface TokenCalculation {
  tokensOut: number;
  pricePerToken: number; // in SOL
  averagePricePerToken: number; // in SOL
  effectiveMultiplier: number;
}

export interface PositionValue {
  currentValue: number; // in SOL
  costBasis: number; // in SOL - current amount at risk
  unrealizedPnL: number; // in SOL
  multiplier: number;
  pricePerToken: number; // in SOL
}

/**
 * Calculate tokens received for a given SOL deposit
 * 
 * Uses the smart contract formula for opinion markets:
 * tokens = (amount × total_pool_after) / option_pool_after
 * 
 * Where:
 * - option_pool_after = virtual_liquidity + total_deposits + net_deposit
 * - total_pool_after = sum of ALL option pools (virtual + real deposits)
 * 
 * IMPORTANT: Must sum across ALL options in the market to match smart contract
 */
export function calculateTokensOut(
  solIn: number, // in lamports
  virtualLiquidity: number, // in lamports - this option's virtual liquidity
  totalDeposits: number, // in lamports - current real deposits on this option
  totalVirtualLiquidity: number, // in lamports - sum of virtual liquidity across ALL options
  totalRealDeposits: number // in lamports - sum of real deposits across ALL options
): TokenCalculation {
  if (virtualLiquidity <= 0) {
    throw new Error('Invalid bonding curve state');
  }

  // Option pool after this deposit (this option only)
  const optionPoolAfter = virtualLiquidity + totalDeposits + solIn;
  
  // Total pool after this deposit (ALL options)
  // = sum of all virtual liquidity + sum of all real deposits + this new deposit
  const totalPoolAfter = totalVirtualLiquidity + totalRealDeposits + solIn;
  
  // Tokens = (amount × total_pool_after) / option_pool_after
  const tokensOut = (solIn * totalPoolAfter) / optionPoolAfter;
  
  // Price per token
  const pricePerToken = optionPoolAfter / totalPoolAfter;
  
  // Average price per token for this transaction
  const averagePricePerToken = solIn / tokensOut;
  
  // Effective multiplier
  const effectiveMultiplier = pricePerToken / averagePricePerToken;
  
  return {
    tokensOut,
    pricePerToken: pricePerToken / LAMPORTS_PER_SOL,
    averagePricePerToken: averagePricePerToken / LAMPORTS_PER_SOL,
    effectiveMultiplier
  };
}

/**
 * Calculate current value of a position based on tokens held
 * 
 * For prediction markets, the multiplier represents your share of the total pool if you win.
 * Formula: (userTokens / totalTokens) × totalDeposits / costBasis
 * 
 * This is different from bonding curve price - it shows your expected payout multiplier.
 */
export function calculatePositionValue(
  userTokens: number,
  costBasis: number, // in lamports - user's current amount at risk
  virtualLiquidity: number, // in lamports
  totalTokens: number,
  optionTotalDeposits: number, // in lamports - total deposits on this option
  totalMarketDeposits?: number // in lamports - total deposits across ALL options (for multiplier)
): PositionValue {
  if (totalTokens <= 0) {
    return {
      currentValue: 0,
      costBasis: costBasis / LAMPORTS_PER_SOL,
      unrealizedPnL: -(costBasis / LAMPORTS_PER_SOL),
      multiplier: 0,
      pricePerToken: 0
    };
  }

  // Current price per token (marginal price from bonding curve)
  const pricePerToken = virtualLiquidity > 0 ? virtualLiquidity / totalTokens : 0;
  
  // For prediction markets, multiplier = your share of the TOTAL MARKET pool if you win
  // Your token percentage × Total MARKET deposits / Your cost basis
  const tokenPercentage = userTokens / totalTokens;
  
  // Use total market deposits for multiplier (winner takes from entire market)
  // If not provided, fall back to option deposits
  const poolForMultiplier = totalMarketDeposits || optionTotalDeposits;
  const expectedPayout = tokenPercentage * poolForMultiplier;
  const multiplier = costBasis > 0 ? expectedPayout / costBasis : 0;
  
  // Current value is based on your expected payout
  const currentValueLamports = expectedPayout;
  
  // Unrealized PnL
  const unrealizedPnL = currentValueLamports - costBasis;
  
  return {
    currentValue: currentValueLamports / LAMPORTS_PER_SOL,
    costBasis: costBasis / LAMPORTS_PER_SOL,
    unrealizedPnL: unrealizedPnL / LAMPORTS_PER_SOL,
    multiplier,
    pricePerToken: pricePerToken / LAMPORTS_PER_SOL
  };
}

/**
 * Calculate expected position after adding more to existing position
 */
export function calculateDoubleDownPosition(
  currentTokens: number,
  currentCostBasis: number, // in lamports - current amount at risk
  additionalSol: number, // in lamports
  virtualLiquidity: number, // in lamports - this option's virtual liquidity
  totalDeposits: number, // in lamports - current real deposits on this option
  currentTotalTokens: number, // current total tokens in the pool
  totalVirtualLiquidity: number, // in lamports - sum across ALL options
  totalRealDeposits: number // in lamports - sum across ALL options
): {
  newTokens: number;
  newCostBasis: number; // in SOL
  newPosition: PositionValue;
  additionalTokens: number;
} {
  // Calculate tokens from additional deposit
  const { tokensOut: additionalTokens } = calculateTokensOut(
    additionalSol,
    virtualLiquidity,
    totalDeposits,
    totalVirtualLiquidity,
    totalRealDeposits
  );
  
  // New position totals
  const newTokens = currentTokens + additionalTokens;
  const newCostBasis = currentCostBasis + additionalSol;
  
  // Calculate new total deposits and tokens on this option after the additional deposit
  const newTotalDepositsOnOption = totalDeposits + additionalSol;
  const newTotalTokensInPool = currentTotalTokens + additionalTokens;
  const newTotalMarketDeposits = totalRealDeposits + additionalSol;
  
  // Calculate position value with updated state
  const newPosition = calculatePositionValue(
    newTokens,
    newCostBasis,
    virtualLiquidity, // Virtual liquidity doesn't change
    newTotalTokensInPool, // Use actual total tokens in pool
    newTotalDepositsOnOption, // Total deposits on option after this deposit
    newTotalMarketDeposits // Total market deposits for multiplier
  );
  
  return {
    newTokens,
    newCostBasis: newCostBasis / LAMPORTS_PER_SOL,
    newPosition,
    additionalTokens
  };
}

/**
 * Calculate expected tokens and position for a new deposit (before any position exists)
 */
export function calculateExpectedPosition(
  depositAmount: number, // in lamports
  virtualLiquidity: number, // in lamports - this option's virtual liquidity
  totalDeposits: number, // in lamports - current real deposits on this option
  totalVirtualLiquidity: number, // in lamports - sum across ALL options
  totalRealDeposits: number, // in lamports - sum across ALL options
  currentTotalTokens: number // current total tokens in this option's pool
): {
  expectedTokens: number;
  expectedValue: PositionValue;
  pricePerToken: number; // in SOL
} {
  // Calculate tokens from deposit
  const { tokensOut: expectedTokens, pricePerToken } = calculateTokensOut(
    depositAmount,
    virtualLiquidity,
    totalDeposits,
    totalVirtualLiquidity,
    totalRealDeposits
  );
  
  // Calculate new total deposits after this deposit
  const newTotalDepositsOnOption = totalDeposits + depositAmount;
  const newTotalMarketDeposits = totalRealDeposits + depositAmount;
  
  // Calculate new total tokens in pool after this deposit
  const newTotalTokensInPool = currentTotalTokens + expectedTokens;
  
  // Calculate position value with updated state
  const expectedValue = calculatePositionValue(
    expectedTokens,
    depositAmount,
    virtualLiquidity, // Virtual liquidity doesn't change
    newTotalTokensInPool, // Total tokens in pool after this deposit
    newTotalDepositsOnOption, // Total deposits on option after this deposit
    newTotalMarketDeposits // Total market deposits for multiplier
  );
  
  return {
    expectedTokens,
    expectedValue,
    pricePerToken
  };
}

/**
 * Calculate projected win payout if user's side wins
 * 
 * Two scenarios:
 * 1. If WINNING (>50%): Simple snapshot - your share × total deposits
 * 2. If LOSING (<50%): Calculate diluted payout if side barely wins (50%+)
 *    - More SOL must flow to your side to win
 *    - This mints new tokens, diluting your share
 *    - Show what you'd get at the point of barely winning
 */
export interface ProjectedWinResult {
  projectedWin: number; // in SOL
  multiplier: number; // projectedWin / costBasis
  isWinning: boolean; // true if user's side is currently winning
}

export function calculateProjectedWin(
  userTokens: number,
  costBasis: number, // in lamports
  userOptionDeposits: number, // in lamports - deposits on user's option
  userOptionTotalTokens: number, // total tokens on user's option
  userOptionVirtualLiquidity: number, // in lamports - virtual liquidity for user's option
  otherOptionDeposits: number, // in lamports - deposits on the other option
  totalVirtualLiquidity: number, // in lamports - sum of virtual liquidity across ALL options
  totalRealDeposits: number // in lamports - sum of real deposits across ALL options
): ProjectedWinResult {
  if (userOptionTotalTokens <= 0 || costBasis <= 0) {
    return {
      projectedWin: 0,
      multiplier: 0,
      isWinning: false
    };
  }

  const isWinning = userOptionDeposits >= otherOptionDeposits;
  
  if (isWinning) {
    // WINNING: Simple snapshot - your share × total market deposits
    const tokenPercentage = userTokens / userOptionTotalTokens;
    const projectedWinLamports = tokenPercentage * totalRealDeposits;
    const projectedWin = projectedWinLamports / LAMPORTS_PER_SOL;
    const multiplier = projectedWinLamports / costBasis;
    
    return {
      projectedWin,
      multiplier,
      isWinning: true
    };
  } else {
    // LOSING: Calculate what you'd get if your side barely wins
    // Deficit = how much more SOL needs to come to your side to reach 50%+
    const minAmountToWin = 0.001 * LAMPORTS_PER_SOL; // Minimum buffer to win (0.001 SOL)
    const deficit = otherOptionDeposits - userOptionDeposits + minAmountToWin;
    
    // Calculate new tokens that would be minted from the deficit
    // Using the bonding curve formula: tokens = (amount × total_pool_after) / option_pool_after
    const newOptionPool = userOptionVirtualLiquidity + userOptionDeposits + deficit;
    const newTotalPool = totalVirtualLiquidity + totalRealDeposits + deficit;
    const newTokensFromDeficit = (deficit * newTotalPool) / newOptionPool;
    
    // New total tokens on user's option
    const newTotalTokens = userOptionTotalTokens + newTokensFromDeficit;
    
    // User's diluted share
    const dilutedTokenPercentage = userTokens / newTotalTokens;
    
    // New total market deposits after deficit
    const newTotalMarketDeposits = totalRealDeposits + deficit;
    
    // Projected win with dilution
    const projectedWinLamports = dilutedTokenPercentage * newTotalMarketDeposits;
    const projectedWin = projectedWinLamports / LAMPORTS_PER_SOL;
    const multiplier = projectedWinLamports / costBasis;
    
    return {
      projectedWin,
      multiplier,
      isWinning: false
    };
  }
}
